import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Users, Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { scrollToSection } from "@/lib/scroll";
import { formatApiErrorDetail, useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(d) {
  return new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", month: "short", day: "numeric" });
}

const typeStyles = {
  Armed: "bg-red-500/10 text-red-400",
  Supervisor: "bg-amber-500/10 text-amber-500",
  Unarmed: "bg-emerald-500/10 text-emerald-400",
  Event: "bg-sky-500/10 text-sky-400",
};

export default function ShiftsBoard() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState(null);
  const [city, setCity] = useState("All");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const [reportShift, setReportShift] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [reportSubject, setReportSubject] = useState("");
  const [reportBusy, setReportBusy] = useState(false);

  const submitReport = async (e) => {
    e.preventDefault();
    setReportBusy(true);
    try {
      const res = await fetch(`${API}/gig/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ shift_id: reportShift.id, subject_email: reportSubject, reason: reportReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success("Report filed — our team reviews every one.");
      setReportShift(null);
      setReportReason("");
      setReportSubject("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReportBusy(false);
    }
  };

  const notify = async (e) => {
    e.preventDefault();
    if (!notifyEmail.trim()) return;
    setNotifyBusy(true);
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: notifyEmail.trim(), role: "guard" }),
      });
      if (!res.ok) throw new Error("join failed");
      setNotifyDone(true);
    } catch {
      scrollToSection("download");
    } finally {
      setNotifyBusy(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/shifts`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setShifts(d?.shifts || []))
      .catch(() => setShifts([]));
  }, []);

  if (!shifts || shifts.length === 0) return null;

  const cities = ["All", ...new Set(shifts.map((s) => s.city))];
  const shown = city === "All" ? shifts : shifts.filter((s) => s.city === city);
  const sym = (s) => (s?.currency === "GBP" ? "£" : "$");
  const totalSpots = shown.reduce((a, s) => a + (s.spots || 0), 0);
  const maxRate = Math.max(...shown.map((s) => s.rate || 0));
  const topShift = shown.find((s) => s.rate === maxRate);

  return (
    <section id="shifts" data-testid="shifts-board-section" className="border-y border-white/5 bg-[#0a0d13] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col justify-between gap-8 md:flex-row md:items-end"
        >
          <div>
            <p className="font-mono-tag text-[11px] text-amber-500">Opening week · live from the platform</p>
            <h2 className="font-display mt-4 max-w-xl text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl">
              Demand is already waiting
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400">
              Real venues, real rates, posted before launch. This is the actual opening-week
              board guards will claim from the moment the app goes live.
            </p>
          </div>
          <div className="flex gap-3">
            {[
              [shown.length, "shifts posted"],
              [totalSpots, "open spots"],
              [`${sym(topShift)}${maxRate}/hr`, "top rate"],
            ].map(([v, l]) => (
              <div key={l} className="rounded-lg border border-white/8 bg-[#0f141c] px-5 py-4 text-center">
                <p data-testid={`board-stat-${l.replace(/\s+/g, "-")}`} className="font-display text-2xl font-black text-slate-50">{v}</p>
                <p className="font-mono-tag mt-1 text-[8px] text-slate-500">{l}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="mt-12 flex flex-wrap items-center gap-2">
          {cities.map((c) => (
            <button
              key={c}
              data-testid={`board-city-${c.toLowerCase()}`}
              onClick={() => setCity(c)}
              className={`rounded-md px-5 py-2.5 font-display text-sm font-bold uppercase transition-all duration-200 ${
                city === c ? "bg-amber-500 text-[#07090e]" : "border border-white/10 text-slate-400 hover:text-slate-50"
              }`}
            >
              {c}
            </button>
          ))}
          {cities.length > 1 && (
            <span className="ml-2 flex items-center gap-3 font-mono-tag text-[9px] text-slate-600">
              Share:
              {cities.filter((c) => c !== "All").map((c) => (
                <a key={c} data-testid={`board-share-${c.toLowerCase()}`} href={`/cities/${c.toLowerCase()}`} className="text-amber-500/70 underline underline-offset-4 transition-colors hover:text-amber-400">
                  {c}
                </a>
              ))}
            </span>
          )}
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-white/8 bg-[#0f141c]">
          {shown.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={`board-shift-${i}`}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="group flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-white/5 px-5 py-4 transition-colors last:border-b-0 hover:bg-white/[0.02] sm:px-7"
            >
              <div className="min-w-[180px] flex-1">
                <p className="font-display text-lg font-bold uppercase leading-tight text-slate-50">{s.venue}</p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                  <MapPin className="h-3 w-3" /> {s.area}, {s.city}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Clock className="h-3.5 w-3.5 text-slate-600" />
                {fmtDate(s.date)} · {s.start}–{s.end}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Users className="h-3.5 w-3.5" /> {s.spots} spot{s.spots === 1 ? "" : "s"}
              </div>
              <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${typeStyles[s.type] || "bg-white/5 text-slate-400"}`}>{s.type}</span>
              <span className="font-display text-xl font-black text-amber-500">{sym(s)}{s.rate}/hr</span>
              {user && (
                <button
                  data-testid={`board-report-${i}`}
                  title="Report a problem with this shift"
                  onClick={() => { setReportShift(s); setReportReason(""); setReportSubject(""); }}
                  className="text-slate-700 transition-colors hover:text-red-400"
                >
                  <Flag className="h-3.5 w-3.5" />
                </button>
              )}
            </motion.div>
          ))}
        </div>

        {reportShift && (
          <div data-testid="report-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setReportShift(null)}>
            <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#0f141c] p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display text-lg font-bold uppercase text-slate-50">Report a problem</h3>
              <p className="mt-1 font-mono-tag text-[9px] text-slate-500">{reportShift.venue} · {reportShift.date}</p>
              <form onSubmit={submitReport} className="mt-5 space-y-3">
                <input
                  data-testid="report-subject-input"
                  value={reportSubject}
                  onChange={(e) => setReportSubject(e.target.value)}
                  placeholder="Who is this about? (email, optional)"
                  className="h-11 w-full rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
                />
                <textarea
                  data-testid="report-reason-input"
                  required
                  minLength={10}
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  placeholder="What happened? Our team reviews every report."
                  rows={4}
                  className="w-full rounded-md border border-white/10 bg-[#07090e] px-4 py-3 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
                />
                <div className="flex gap-3">
                  <button
                    data-testid="report-submit-btn"
                    type="submit"
                    disabled={reportBusy}
                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-md bg-red-500 font-display text-sm font-bold uppercase text-white transition-all hover:bg-red-400 disabled:opacity-50"
                  >
                    {reportBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    File report
                  </button>
                  <button
                    data-testid="report-cancel-btn"
                    type="button"
                    onClick={() => setReportShift(null)}
                    className="rounded-md border border-white/15 px-5 font-display text-sm font-bold uppercase text-slate-400 hover:text-slate-50"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-center font-mono-tag text-[8px] text-slate-600">False reports waste review time — the rules apply to reporters too.</p>
              </form>
            </div>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-8 flex flex-col gap-5"
        >
          <p className="font-mono-tag text-[9px] text-slate-600">
            Claiming opens at launch · GPS check-in required · rule 01 applies
          </p>
          {notifyDone ? (
            <p data-testid="board-notify-success" className="flex items-center gap-2 text-sm text-emerald-400">
              You're in — we'll email you the moment claiming opens.
            </p>
          ) : (
            <form data-testid="board-notify-form" onSubmit={notify} className="flex flex-col gap-3 sm:flex-row">
              <input
                data-testid="board-notify-email"
                type="email"
                required
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-13 flex-1 rounded-md border border-white/10 bg-[#0f141c] px-5 py-3.5 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none sm:max-w-sm"
              />
              <button
                data-testid="board-notify-btn"
                type="submit"
                disabled={notifyBusy}
                className="rounded-md bg-amber-500 px-6 py-3.5 font-display text-sm font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 hover:shadow-[0_0_24px_rgba(245,158,11,0.35)] disabled:opacity-50"
              >
                {notifyBusy ? "Joining…" : "Notify me when claiming opens"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
